import * as React from "react"
import { ChevronDown, ChevronRight, type LucideIcon } from "lucide-react"
import { Link, usePage } from "@inertiajs/react"

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"

interface NavCollapsibleProps extends React.ComponentPropsWithoutRef<typeof SidebarGroup> {
  title: string
  sectionKey: string
  icon?: LucideIcon
  items: {
    title: string
    url: string
    icon?: LucideIcon
    activePattern?: string
  }[]
  defaultExpanded?: boolean
}

export function NavCollapsible({
  title,
  sectionKey,
  icon: SectionIcon,
  items,
  defaultExpanded = false,
  ...props
}: NavCollapsibleProps) {
  const { url } = usePage()
  const { state } = useSidebar()

  // Check if current page is within this section
  const isCurrentSection = items.some((item) => {
    const pattern = item.activePattern || item.url
    return url.startsWith(pattern)
  })

  // Initialize expanded state from localStorage or default
  const [isExpanded, setIsExpanded] = React.useState(() => {
    if (typeof window === 'undefined') return defaultExpanded

    const stored = localStorage.getItem(`sidebar-section-${sectionKey}`)
    if (stored !== null) {
      return stored === 'true'
    }

    // If current page is in this section, expand it
    return isCurrentSection || defaultExpanded
  })

  // Update localStorage when expanded state changes
  React.useEffect(() => {
    localStorage.setItem(`sidebar-section-${sectionKey}`, String(isExpanded))
  }, [isExpanded, sectionKey])

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <SidebarGroup {...props}>
      <button
        onClick={toggleExpanded}
        className={cn(
          "flex w-full items-center gap-2 px-2 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors",
          "hover:bg-sidebar-accent rounded-md",
          "text-sidebar-foreground/70 hover:text-sidebar-foreground",
          state === "collapsed" && "justify-center"
        )}
      >
        {state === "expanded" && (
          <>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 transition-transform" />
            ) : (
              <ChevronRight className="h-4 w-4 transition-transform" />
            )}
          </>
        )}
        {SectionIcon && <SectionIcon className="h-4 w-4 text-[#5589ed]" />}
        {state === "expanded" && <span className="flex-1 text-left">{title}</span>}
      </button>

      <div
        className={cn(
          "grid transition-all duration-300 ease-in-out",
          isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          <SidebarGroupContent className="mt-1">
            <SidebarMenu>
              {items.map((item) => {
                const pattern = item.activePattern || item.url
                const isActive = url.startsWith(pattern)

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.title}
                      isActive={isActive}
                      className={isActive ? "bg-[#5589ed]/10 text-[#5589ed] hover:bg-[#5589ed]/15 font-medium" : "hover:bg-gray-100"}
                    >
                      <Link href={item.url}>
                        {item.icon && <item.icon className={isActive ? "text-[#5589ed]" : ""} />}
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </div>
      </div>
    </SidebarGroup>
  )
}
